import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, ChevronDown, X, Check, AlertTriangle,
  Type as TypeIcon, Image as ImageIcon, Film, FileText, MapPin, LayoutGrid, MessageSquare,
} from 'lucide-react';
import clsx from 'clsx';
import { templatesService, Template, TemplateCategory } from '@/services/templates.service';

type TypeChipKind = 'CAROUSEL' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION' | 'NONE';

function typeKindOf(t: Template): TypeChipKind {
  if (t.type === 'CAROUSEL') return 'CAROUSEL';
  return (t.headerType ?? 'NONE') as TypeChipKind;
}

const TYPE_META: Record<TypeChipKind, { label: string; Icon: React.ComponentType<{ size?: number; className?: string }>; cls: string }> = {
  CAROUSEL: { label: 'Carousel', Icon: LayoutGrid, cls: 'bg-info-soft text-info' },
  IMAGE:    { label: 'Image',    Icon: ImageIcon, cls: 'bg-primary-soft text-primary' },
  VIDEO:    { label: 'Video',    Icon: Film,      cls: 'bg-danger-soft text-danger' },
  DOCUMENT: { label: 'Document', Icon: FileText,  cls: 'bg-warning-soft text-warning' },
  LOCATION: { label: 'Location', Icon: MapPin,    cls: 'bg-success-soft text-success' },
  TEXT:     { label: 'Text',     Icon: TypeIcon,  cls: 'bg-surface-alt text-text-secondary' },
  NONE:     { label: 'Text',     Icon: MessageSquare, cls: 'bg-surface-alt text-text-secondary' },
};

const TypeChip: React.FC<{ kind: TypeChipKind; compact?: boolean }> = ({ kind, compact }) => {
  const { label, Icon, cls } = TYPE_META[kind] ?? TYPE_META.NONE;
  return (
    <span
      className={clsx(
        'inline-flex shrink-0 items-center gap-1 rounded font-semibold uppercase tracking-wide',
        compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-0.5 text-2xs',
        cls,
      )}
      title={`Header: ${label}`}
    >
      <Icon size={compact ? 10 : 11} />
      {label}
    </span>
  );
};

interface Props {
  value?: string; // templateUid
  onChange: (uid: string, template: Template | null) => void;
  /** Status to filter by (defaults to APPROVED for campaigns; pass undefined to show all). */
  status?: 'APPROVED' | 'PENDING' | 'DRAFT';
  category?: TemplateCategory;
  language?: string;
  /** Restrict results to a specific WhatsApp account. Campaigns/broadcasts/etc.
   *  always pass this so a template approved against a different WABA can't
   *  be selected — Meta would reject the send at runtime. */
  accountUid?: string;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
}

/**
 * Searchable template picker. Debounces queries, paginates against the
 * backend so 1000s of templates remain usable.
 */
export const TemplatePicker: React.FC<Props> = ({
  value, onChange, status = 'APPROVED', category, language, accountUid, placeholder, invalid, disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [items, setItems] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Template | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Debounce query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Initial load of selected template (so the trigger label can show details)
  useEffect(() => {
    if (!value) { setSelected(null); return; }
    if (selected?.templateUid === value) return;
    templatesService.get(value).then(setSelected).catch(() => setSelected(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Search
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    templatesService
      .list({ q: debouncedQ || undefined, status, category, language, accountUid, limit: 25 })
      .then((r) => { if (!cancelled) setItems(r.items); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, debouncedQ, status, category, language, accountUid]);

  // Click outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  // Focus search on open
  useEffect(() => {
    if (open) setTimeout(() => searchRef.current?.focus(), 0);
    else setQ('');
  }, [open]);

  const triggerLabel = useMemo(() => {
    if (!selected) return placeholder ?? 'Select a template…';
    return selected.name;
  }, [selected, placeholder]);

  const pick = (t: Template) => {
    setSelected(t);
    onChange(t.templateUid, t);
    setOpen(false);
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected(null);
    onChange('', null);
  };

  const isSeed = selected?.waTemplateId?.startsWith('seed_') ?? false;

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className={clsx(
          'flex h-10 w-full items-center gap-2 rounded-lg border bg-surface px-3 text-sm transition-colors',
          'hover:border-border-strong focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft',
          'disabled:opacity-60 disabled:cursor-not-allowed',
          invalid ? 'border-danger focus:border-danger focus:ring-danger-soft' : 'border-border',
        )}
      >
        <Search size={14} className="shrink-0 text-text-muted" />
        <span className={clsx('flex-1 truncate text-left', selected ? 'text-text-primary' : 'text-text-muted')}>
          {triggerLabel}
        </span>
        {selected && (
          <>
            <span className="hidden shrink-0 items-center gap-1.5 text-2xs sm:flex">
              <TypeChip kind={typeKindOf(selected)} compact />
              <span className="rounded bg-surface-alt px-1.5 py-0.5 font-mono uppercase text-text-secondary">{selected.language}</span>
              <span className="rounded bg-surface-alt px-1.5 py-0.5 capitalize text-text-secondary">{selected.category}</span>
            </span>
            {!disabled && (
              <button type="button" onClick={clear} className="shrink-0 rounded-full p-0.5 text-text-muted hover:bg-surface-alt hover:text-text-primary">
                <X size={13} />
              </button>
            )}
          </>
        )}
        <ChevronDown size={14} className={clsx('shrink-0 text-text-muted transition-transform', open && 'rotate-180')} />
      </button>

      {isSeed && selected && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-soft px-2.5 py-1.5">
          <AlertTriangle size={12} className="mt-0.5 shrink-0 text-warning" />
          <p className="text-2xs text-warning leading-relaxed">
            Demo template — has a placeholder Meta ID. Click <strong>Sync from Meta</strong> on the templates page to pull the real one before sending.
          </p>
        </div>
      )}

      {open && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-xl border border-border bg-surface shadow-elevated animate-fade-in">
          {/* Search */}
          <div className="border-b border-border p-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
              <input
                ref={searchRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name…"
                className="h-9 w-full rounded-lg border border-border bg-surface-alt pl-9 pr-3 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary-soft"
              />
            </div>
          </div>

          {/* List */}
          <ul className="max-h-80 overflow-y-auto py-1">
            {loading && (
              <li className="px-3 py-3">
                <div className="skeleton h-10 rounded-lg" />
              </li>
            )}
            {!loading && items.length === 0 && (
              <li className="px-4 py-6 text-center text-xs text-text-muted">
                {debouncedQ
                  ? <>No matches for &ldquo;{debouncedQ}&rdquo;</>
                  : `No ${status?.toLowerCase() ?? ''} templates yet.`}
              </li>
            )}
            {!loading && items.map((t) => {
              const active = t.templateUid === value;
              const seed = t.waTemplateId?.startsWith('seed_');
              return (
                <li key={t.templateUid}>
                  <button
                    type="button"
                    onClick={() => pick(t)}
                    className={clsx(
                      'flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors',
                      active ? 'bg-primary-soft' : 'hover:bg-surface-alt',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className={clsx('truncate text-sm font-medium', active ? 'text-primary' : 'text-text-primary')}>
                          {t.name}
                        </span>
                        <TypeChip kind={typeKindOf(t)} compact />
                        {seed && <AlertTriangle size={11} className="shrink-0 text-warning" />}
                      </div>
                      <div className="mt-0.5 flex items-center gap-1.5 text-2xs text-text-muted">
                        <span className="rounded bg-surface-alt px-1.5 py-0.5 font-mono uppercase">{t.language}</span>
                        <span className="rounded bg-surface-alt px-1.5 py-0.5 capitalize">{t.category}</span>
                        <span className="truncate">{t.body.slice(0, 60)}…</span>
                      </div>
                    </div>
                    {active && <Check size={14} className="mt-0.5 shrink-0 text-primary" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};
